import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Route, 
  Car, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  Filter,
  Eye,
  Download,
  Play
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { GpsTrip, TripsFilters } from '@/hooks/use-gps-trips';

interface TripsListProps {
  trips: GpsTrip[];
  loading?: boolean;
  onTripSelect?: (trip: GpsTrip) => void;
  onTripReplay?: (trip: GpsTrip) => void;
  onFiltersChange?: (filters: TripsFilters) => void;
}

export const TripsList: React.FC<TripsListProps> = ({
  trips,
  loading = false,
  onTripSelect,
  onTripReplay,
  onFiltersChange
}) => {
  const [filters, setFilters] = useState<TripsFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date>();
  const [dateTo, setDateTo] = useState<Date>();

  const handleFilterChange = (key: keyof TripsFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    handleFilterChange('startDate', date ? format(date, 'yyyy-MM-dd') : undefined);
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    handleFilterChange('endDate', date ? format(date, 'yyyy-MM-dd') : undefined);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDistance = (km: number) => {
    return `${km.toFixed(1)} km`;
  };

  const getStatusBadge = (trip: GpsTrip) => {
    const duration = trip.duration_minutes;
    if (duration < 30) {
      return <Badge variant="secondary">Curta</Badge>;
    } else if (duration < 120) {
      return <Badge variant="default">Média</Badge>;
    } else {
      return <Badge variant="outline">Longa</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5 text-primary" />
            <CardTitle>Histórico de Viagens</CardTitle>
            <Badge variant="outline">{trips.length} viagens</Badge>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
            {/* Date From */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Inicial</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateFrom && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, "PPP", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={handleDateFromChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Final</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, "PPP", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={handleDateToChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Vehicle Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Veículo</label>
              <Select
                value={filters.vehicleId || "all"}
                onValueChange={(value) => handleFilterChange('vehicleId', value === "all" ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os veículos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os veículos</SelectItem>
                  {/* Add vehicle options based on available vehicles */}
                </SelectContent>
              </Select>
            </div>

            {/* Distance Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Distância Mínima (km)</label>
              <Input
                type="number"
                placeholder="0"
                value={filters.minDistance || ''}
                onChange={(e) => handleFilterChange('minDistance', e.target.value ? Number(e.target.value) : undefined)}
              />
            </div>
          </div>
        )}

        {/* Trips Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Veículo</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Fim</TableHead>
                <TableHead>Duração</TableHead>
                <TableHead>Distância</TableHead>
                <TableHead>Tempo Parado</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Carregando viagens...
                  </TableCell>
                </TableRow>
              ) : trips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhuma viagem encontrada
                  </TableCell>
                </TableRow>
              ) : (
                trips.map((trip) => (
                  <TableRow key={trip.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Car className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">
                            {trip.vehicle_plate || trip.device_name || 'N/A'}
                          </div>
                          {trip.vehicle_make && trip.vehicle_model && (
                            <div className="text-sm text-muted-foreground">
                              {trip.vehicle_make} {trip.vehicle_model}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-green-600" />
                        <div>
                          <div className="text-sm">
                            {format(new Date(trip.start_time), 'dd/MM HH:mm')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {trip.start_location?.address?.formatted || 
                             `${trip.start_location.lat.toFixed(4)}, ${trip.start_location.lon.toFixed(4)}`}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-red-600" />
                        <div>
                          <div className="text-sm">
                            {format(new Date(trip.end_time), 'dd/MM HH:mm')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {trip.end_location?.address?.formatted || 
                             `${trip.end_location.lat.toFixed(4)}, ${trip.end_location.lon.toFixed(4)}`}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDuration(trip.duration_minutes)}
                      </div>
                    </TableCell>
                    <TableCell>{formatDistance(trip.distance_km)}</TableCell>
                    <TableCell>
                      {trip.idle_time_minutes > 0 ? formatDuration(trip.idle_time_minutes) : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(trip)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onTripSelect?.(trip)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {onTripReplay && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onTripReplay(trip)}
                            title="Replay da viagem"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};